import type { NavigateFunction } from 'react-router'

/**
 * Сервіс навігації для роботи з React Router поза компонентами
 *
 * Дозволяє використовувати навігацію React Router в сервісах, менеджерах та задачах,
 * де немає доступу до хуків React.
 *
 * @example
 * ```ts
 * import { navigationService } from '@/Core/Services/NavigationService'
 *
 * navigationService.navigateTo('/users/123')
 * ```
 */
export class NavigationService {
    private navigate: NavigateFunction | null = null

    /**
     * Ініціалізація сервісу з функцією navigate від React Router
     * Викликається автоматично в Layout компоненті через хук
     */
    initialize(navigate: NavigateFunction): void {
        this.navigate = navigate
    }

    /**
     * Навігація на вказаний URL
     *
     * @param url - URL для навігації (відносний або абсолютний)
     * @param options - Опції навігації (replace, state)
     * @throws Error якщо сервіс не ініціалізовано
     */
    navigateTo(url: string, options?: { replace?: boolean; state?: any }): void {
        if (!this.navigate) {
            console.error('NavigationService не ініціалізовано! Переконайтеся, що викликано initialize()')
            return
        }

        this.navigate(url, options)
    }

    /**
     * Перевірка чи сервіс готовий до роботи
     */
    isReady(): boolean {
        return this.navigate !== null
    }

    /**
     * Навігація назад
     */
    goBack(): void {
        if (!this.navigate) {
            console.error('NavigationService не ініціалізовано!')
            return
        }

        this.navigate(-1)
    }

    /**
     * Навігація вперед
     */
    goForward(): void {
        if (!this.navigate) {
            console.error('NavigationService не ініціалізовано!')
            return
        }

        this.navigate(1)
    }
}

/**
 * Singleton екземпляр NavigationService
 */
let _navigationService: NavigationService | null = null

/**
 * Фабрична функція для створення NavigationService
 */
export function createNavigationService(): NavigationService {
    return new NavigationService()
}

/**
 * Глобальний екземпляр NavigationService з ледачою ініціалізацією
 */
export const navigationService = new Proxy({} as NavigationService, {
    get(target, prop) {
        // Ленива ініціалізація при першому зверненні
        if (!_navigationService) {
            _navigationService = createNavigationService()
        }

        const value = (_navigationService as any)[prop]

        // Якщо це метод - прив'язуємо контекст
        if (typeof value === 'function') {
            return value.bind(_navigationService)
        }

        return value
    },
    set(target, prop, value) {
        // Ленива ініціалізація при першому зверненні
        if (!_navigationService) {
            _navigationService = createNavigationService()
        }

        ;(_navigationService as any)[prop] = value
        return true
    },
})
